import { nowUtc } from '@/lib/clock';
import { newUuid } from '@/lib/ids';
import { db as defaultDb, type GentleTaskDatabase } from '@/data/db/database';
import { SETTINGS_ID } from '@/data/db/settings.schema';
import type {
  BoardPlacement,
  LocalDate,
  MatrixPlacement,
  StickerRef,
  Task,
  TaskKind,
  TaskPriority,
  TaskStatus,
  Uuid,
} from '@/data/db';
import { InvariantError, assertTask } from '@/data/invariants';
import { nextOccurrence } from '@/features/tasks/recurrence';
import { appendProgressEntry } from '@/features/tasks/progress-log';
import type { RecurrenceFrequency, RecurrenceRule, LocalDate as LocalDateType } from '@/data/db';
import { NotFoundError, bumpAudit, markDataMutation, newAudit } from './support';

/** UI 的 status 下拉只能提供這三個（data-model 3.2）。 */
const SELECTABLE_STATUSES: readonly TaskStatus[] = ['new', 'in_progress', 'waiting'];

export interface CreateTaskInput {
  subject: string;
  kind?: TaskKind;
  description?: string | null;
  priority?: TaskPriority;
  dueDate?: LocalDate | null;
  startDate?: LocalDate | null;
  topic?: string | null;
  taskTypeId?: Uuid | null;
  sourceTypeId?: Uuid | null;
  tagIds?: Uuid[];
  stickers?: StickerRef[];
  linkedTaskId?: Uuid | null;
  estimatedMinutes?: number | null;
  /** 不指定時使用設定的預設欄位。 */
  columnId?: Uuid;
  /** 傳 false 就不放上看板。 */
  placeOnBoard?: boolean;
}

/** 可以自由修改的欄位；status 與各種時間戳記只能經由專用操作變更。 */
export type UpdatableTaskFields = Partial<
  Pick<
    Task,
    | 'subject'
    | 'description'
    | 'priority'
    | 'taskTypeId'
    | 'sourceTypeId'
    | 'topic'
    | 'tagIds'
    | 'stickers'
    | 'startDate'
    | 'dueDate'
    | 'progress'
    | 'estimatedMinutes'
    | 'actualMinutes'
    | 'linkedTaskId'
  >
>;

export interface RecurrenceInput {
  frequency: RecurrenceFrequency;
  weekdays?: number[] | null;
  monthDay?: number | null;
  yearMonth?: number | null;
  yearDay?: number | null;
  endDate?: LocalDateType | null;
}

export function createTaskRepository(db: GentleTaskDatabase = defaultDb) {
  const writeTables = [
    db.tasks,
    db.boardColumns,
    db.boardPlacements,
    db.matrixPlacements,
    db.recurrenceRules,
    db.taskContacts,
    db.settings,
  ];

  async function nextNumber(): Promise<number> {
    // 包含垃圾桶內的資料一起算，避免刪掉最大號之後撞號（T8）。
    const highest = await db.tasks.orderBy('number').last();
    return (highest?.number ?? 0) + 1;
  }

  async function activeNormalColumnIds(): Promise<Uuid[]> {
    const columns = await db.boardColumns.orderBy('order').toArray();
    return columns
      .filter((column) => column.kind === 'normal' && column.archivedAt === null)
      .map((column) => column.id);
  }

  async function completedQueueId(): Promise<Uuid> {
    const columns = await db.boardColumns.toArray();
    const queue = columns.find((column) => column.kind === 'completed_queue');
    if (!queue) throw new NotFoundError('完成佇列', 'completed_queue');
    return queue.id;
  }

  async function resolveReturnColumnId(preferred: Uuid | null): Promise<Uuid> {
    const active = await activeNormalColumnIds();
    if (preferred && active.includes(preferred)) return preferred;

    const settings = await db.settings.get(SETTINGS_ID);
    const configured = settings?.defaultBoardColumnId ?? null;
    if (configured && active.includes(configured)) return configured;

    const leftmost = active[0];
    if (!leftmost) throw new NotFoundError('啟用中的一般欄位', 'any');
    return leftmost;
  }

  async function nextOrderInColumn(columnId: Uuid): Promise<number> {
    const placements = await db.boardPlacements.where('columnId').equals(columnId).toArray();
    return placements.reduce((max, placement) => Math.max(max, placement.order), -1) + 1;
  }

  /** T7：linkedTaskId 必須指向存在且 kind 為 task 的資料。 */
  async function assertLinkTarget(linkedTaskId: Uuid | null): Promise<void> {
    if (!linkedTaskId) return;
    const target = await db.tasks.get(linkedTaskId);
    if (!target || target.kind !== 'task') {
      throw new InvariantError([
        { code: 'T7', message: 'linkedTaskId 必須指向存在的任務，不能指向便條' },
      ]);
    }
  }

  async function requireTask(id: Uuid): Promise<Task> {
    const task = await db.tasks.get(id);
    if (!task) throw new NotFoundError('任務', id);
    return task;
  }

  async function create(input: CreateTaskInput): Promise<Task> {
    const timestamp = nowUtc();
    const kind: TaskKind = input.kind ?? 'task';
    const isNote = kind === 'note';

    const task: Task = {
      id: newUuid(),
      number: 0,
      kind,
      subject: input.subject,
      description: input.description ?? null,
      status: 'new',
      priority: input.priority ?? 'normal',
      // 便條不使用這些欄位（T5）。
      taskTypeId: isNote ? null : (input.taskTypeId ?? null),
      sourceTypeId: isNote ? null : (input.sourceTypeId ?? null),
      topic: input.topic ?? null,
      tagIds: input.tagIds ?? [],
      stickers: input.stickers ?? [],
      startDate: input.startDate ?? null,
      dueDate: input.dueDate ?? null,
      waitingSince: null,
      linkedTaskId: isNote ? (input.linkedTaskId ?? null) : null,
      recurrenceRuleId: null,
      progress: null,
      estimatedMinutes: isNote ? null : (input.estimatedMinutes ?? null),
      actualMinutes: null,
      completedAt: null,
      cancelledAt: null,
      cancelReason: null,
      archivedAt: null,
      removedFromBoardAt: null,
      deletedAt: null,
      ...newAudit(timestamp),
    };

    await db.transaction('rw', writeTables, async () => {
      await assertLinkTarget(task.linkedTaskId);
      task.number = await nextNumber();
      assertTask(task);
      await db.tasks.add(task);

      if (input.placeOnBoard !== false) {
        const columnId = input.columnId ?? (await resolveReturnColumnId(null));
        const placement: BoardPlacement = {
          id: newUuid(),
          taskId: task.id,
          columnId,
          order: await nextOrderInColumn(columnId),
          columnIdBeforeCompletion: null,
          ...newAudit(timestamp),
        };
        await db.boardPlacements.add(placement);
      }

      // 尚未分類的卡片集中在四象限模式下方的暫存區（spec 13.7）。
      const matrix: MatrixPlacement = {
        id: newUuid(),
        taskId: task.id,
        quadrant: 'unclassified',
        order: await db.matrixPlacements.where('quadrant').equals('unclassified').count(),
        ...newAudit(timestamp),
      };
      await db.matrixPlacements.add(matrix);

      await markDataMutation(db, timestamp);
    });

    return task;
  }

  async function update(id: Uuid, patch: UpdatableTaskFields): Promise<Task> {
    const timestamp = nowUtc();
    let result!: Task;

    await db.transaction('rw', writeTables, async () => {
      const current = await requireTask(id);
      const next: Task = { ...current, ...patch, ...bumpAudit(current, timestamp) };
      if (patch.linkedTaskId !== undefined) await assertLinkTarget(next.linkedTaskId);
      assertTask(next);
      await db.tasks.put(next);
      await markDataMutation(db, timestamp);
      result = next;
    });

    return result;
  }

  /**
   * 卡片上的「記一筆進度」（spec 13.5）。
   *
   * 在交易內重讀說明再接上去，這樣同時在別的地方編輯說明也不會被整段覆蓋掉。
   * 空白的進度直接忽略，不留下只有時間戳記的空行。
   */
  async function appendProgress(id: Uuid, note: string, at: Date = new Date()): Promise<Task> {
    const timestamp = nowUtc();
    let result!: Task;

    await db.transaction('rw', writeTables, async () => {
      const current = await requireTask(id);
      const description = note.trim() ? appendProgressEntry(current.description, note, at) : null;
      const next: Task = description
        ? { ...current, description, ...bumpAudit(current, timestamp) }
        : current;

      if (description) {
        assertTask(next);
        await db.tasks.put(next);
        await markDataMutation(db, timestamp);
      }
      result = next;
    });

    return result;
  }

  /**
   * 單純改 status，**不搬卡片、不寫入結束時間戳記**（spec 9.1）。
   * resolved、closed、cancelled 只能經由 complete、close、cancel 進入。
   */
  async function setStatus(id: Uuid, status: TaskStatus): Promise<void> {
    if (!SELECTABLE_STATUSES.includes(status)) {
      throw new Error(`${status} 只能經由明確的結束操作進入，不能從下拉選單選擇`);
    }

    const timestamp = nowUtc();
    await db.transaction('rw', writeTables, async () => {
      const current = await requireTask(id);
      const next: Task = {
        ...current,
        status,
        waitingSince: status === 'waiting' ? (current.waitingSince ?? timestamp) : null,
        ...bumpAudit(current, timestamp),
      };
      assertTask(next);
      await db.tasks.put(next);
      await markDataMutation(db, timestamp);
    });
  }

  /** 把卡片移進完成佇列，並記住原本的欄位供之後復位。 */
  async function moveIntoQueue(taskId: Uuid, timestamp: string): Promise<void> {
    const queueId = await completedQueueId();
    const current = await db.boardPlacements.where('taskId').equals(taskId).first();
    if (!current) return; // 不在看板上的任務照常可以完成。
    if (current.columnId === queueId) return;

    await db.boardPlacements.put({
      ...current,
      columnId: queueId,
      order: await nextOrderInColumn(queueId),
      columnIdBeforeCompletion: current.columnId,
      ...bumpAudit(current, timestamp),
    });
  }

  /** 離開完成佇列，回到原欄位；原欄位不存在或已封存時退回預設欄位。 */
  async function moveOutOfQueue(taskId: Uuid, timestamp: string): Promise<void> {
    const current = await db.boardPlacements.where('taskId').equals(taskId).first();
    if (!current) return;

    const target = await resolveReturnColumnId(current.columnIdBeforeCompletion);
    await db.boardPlacements.put({
      ...current,
      columnId: target,
      order: await nextOrderInColumn(target),
      columnIdBeforeCompletion: null,
      ...bumpAudit(current, timestamp),
    });
  }

  async function complete(id: Uuid): Promise<void> {
    const timestamp = nowUtc();
    await db.transaction('rw', writeTables, async () => {
      const current = await requireTask(id);
      const next: Task = {
        ...current,
        status: 'resolved',
        completedAt: timestamp,
        cancelledAt: null,
        cancelReason: null,
        waitingSince: null,
        ...bumpAudit(current, timestamp),
      };
      assertTask(next);
      await db.tasks.put(next);
      await moveIntoQueue(id, timestamp);
      await generateNextOccurrence(next, timestamp);
      await markDataMutation(db, timestamp);
    });
  }

  /** 確認結案；completedAt 不變（data-model 3.3）。 */
  async function close(id: Uuid): Promise<void> {
    const timestamp = nowUtc();
    await db.transaction('rw', writeTables, async () => {
      const current = await requireTask(id);
      const next: Task = {
        ...current,
        status: 'closed',
        completedAt: current.completedAt ?? timestamp,
        cancelledAt: null,
        cancelReason: null,
        waitingSince: null,
        ...bumpAudit(current, timestamp),
      };
      assertTask(next);
      await db.tasks.put(next);
      await moveIntoQueue(id, timestamp);
      await markDataMutation(db, timestamp);
    });
  }

  async function cancel(id: Uuid, reason?: string): Promise<void> {
    const timestamp = nowUtc();
    await db.transaction('rw', writeTables, async () => {
      const current = await requireTask(id);
      const next: Task = {
        ...current,
        status: 'cancelled',
        cancelledAt: timestamp,
        cancelReason: reason ?? null,
        completedAt: null,
        waitingSince: null,
        ...bumpAudit(current, timestamp),
      };
      assertTask(next);
      await db.tasks.put(next);
      await moveIntoQueue(id, timestamp);
      await markDataMutation(db, timestamp);
    });
  }

  /** 取消完成與取消取消共用；status 回到處理中，卡片回原欄位。 */
  async function uncomplete(id: Uuid): Promise<void> {
    const timestamp = nowUtc();
    await db.transaction('rw', writeTables, async () => {
      const current = await requireTask(id);
      const next: Task = {
        ...current,
        status: 'in_progress',
        completedAt: null,
        cancelledAt: null,
        cancelReason: null,
        waitingSince: null,
        ...bumpAudit(current, timestamp),
      };
      assertTask(next);
      await db.tasks.put(next);
      await moveOutOfQueue(id, timestamp);
      await markDataMutation(db, timestamp);
    });
  }

  /** 只刪看板位置，不改任務狀態、不進垃圾桶（spec 17.1）。 */
  async function removeFromBoard(id: Uuid): Promise<void> {
    const timestamp = nowUtc();
    await db.transaction('rw', writeTables, async () => {
      const current = await requireTask(id);
      const placement = await db.boardPlacements.where('taskId').equals(id).first();
      if (placement) await db.boardPlacements.delete(placement.id);
      await db.tasks.put({
        ...current,
        removedFromBoardAt: timestamp,
        ...bumpAudit(current, timestamp),
      });
      await markDataMutation(db, timestamp);
    });
  }

  async function putOnBoard(id: Uuid, columnId: Uuid): Promise<void> {
    const timestamp = nowUtc();
    await db.transaction('rw', writeTables, async () => {
      const current = await requireTask(id);
      const existing = await db.boardPlacements.where('taskId').equals(id).first();
      if (existing) {
        await db.boardPlacements.put({
          ...existing,
          columnId,
          order: await nextOrderInColumn(columnId),
          columnIdBeforeCompletion: null,
          ...bumpAudit(existing, timestamp),
        });
      } else {
        await db.boardPlacements.add({
          id: newUuid(),
          taskId: id,
          columnId,
          order: await nextOrderInColumn(columnId),
          columnIdBeforeCompletion: null,
          ...newAudit(timestamp),
        });
      }
      await db.tasks.put({
        ...current,
        removedFromBoardAt: null,
        ...bumpAudit(current, timestamp),
      });
      await markDataMutation(db, timestamp);
    });
  }

  async function archive(id: Uuid, archived = true): Promise<void> {
    const timestamp = nowUtc();
    await db.transaction('rw', writeTables, async () => {
      const current = await requireTask(id);
      await db.tasks.put({
        ...current,
        archivedAt: archived ? timestamp : null,
        ...bumpAudit(current, timestamp),
      });
      await markDataMutation(db, timestamp);
    });
  }

  async function softDelete(id: Uuid): Promise<void> {
    const timestamp = nowUtc();
    await db.transaction('rw', writeTables, async () => {
      const current = await requireTask(id);
      await db.tasks.put({ ...current, deletedAt: timestamp, ...bumpAudit(current, timestamp) });
      await markDataMutation(db, timestamp);
    });
  }

  async function restore(id: Uuid): Promise<void> {
    const timestamp = nowUtc();
    await db.transaction('rw', writeTables, async () => {
      const current = await requireTask(id);
      await db.tasks.put({ ...current, deletedAt: null, ...bumpAudit(current, timestamp) });
      await markDataMutation(db, timestamp);
    });
  }

  /** 一般查詢預設排除垃圾桶內容（data-model 2.4）。 */
  async function listActive(): Promise<Task[]> {
    const all = await db.tasks.orderBy('number').toArray();
    return all.filter((task) => task.deletedAt === null);
  }

  async function listDeleted(): Promise<Task[]> {
    const all = await db.tasks.orderBy('number').toArray();
    return all.filter((task) => task.deletedAt !== null);
  }

  async function get(id: Uuid): Promise<Task | undefined> {
    return db.tasks.get(id);
  }

  /** 某個任務的補充便條；刪除任務時只解除關聯，不刪便條。 */
  async function listNotesFor(taskId: Uuid): Promise<Task[]> {
    const notes = await db.tasks.where('linkedTaskId').equals(taskId).toArray();
    return notes.filter((note) => note.deletedAt === null);
  }

  /**
   * 完成後才產生下一次（spec 9.4）。
   * 使用者若不完成，系統不會自動產生，也不另行提醒——這是第一版刻意接受的行為。
   */
  async function generateNextOccurrence(completed: Task, timestamp: string): Promise<void> {
    if (!completed.recurrenceRuleId) return;
    const rule = await db.recurrenceRules.get(completed.recurrenceRuleId);
    if (!rule) return;

    // 沒有到期日就算不出下一次的日期。
    if (!completed.dueDate) return;
    const nextDue = nextOccurrence(rule, completed.dueDate);
    if (!nextDue) return;

    const next: Task = {
      ...completed,
      id: newUuid(),
      number: await nextNumber(),
      status: 'new',
      dueDate: nextDue,
      startDate: null,
      // 不複製這些欄位（data-model 3.5）。
      waitingSince: null,
      progress: null,
      actualMinutes: null,
      completedAt: null,
      cancelledAt: null,
      cancelReason: null,
      archivedAt: null,
      removedFromBoardAt: null,
      deletedAt: null,
      ...newAudit(timestamp),
    };
    assertTask(next);
    await db.tasks.add(next);

    // 複製聯絡人關聯。
    const links = await db.taskContacts.where('taskId').equals(completed.id).toArray();
    for (const link of links) {
      await db.taskContacts.add({
        ...link,
        id: newUuid(),
        taskId: next.id,
        ...newAudit(timestamp),
      });
    }

    // 放上看板與四象限。
    const columnId = await resolveReturnColumnId(null);
    await db.boardPlacements.add({
      id: newUuid(),
      taskId: next.id,
      columnId,
      order: await nextOrderInColumn(columnId),
      columnIdBeforeCompletion: null,
      ...newAudit(timestamp),
    });
    await db.matrixPlacements.add({
      id: newUuid(),
      taskId: next.id,
      quadrant: 'unclassified',
      order: await db.matrixPlacements.where('quadrant').equals('unclassified').count(),
      ...newAudit(timestamp),
    });
  }

  async function setRecurrence(id: Uuid, input: RecurrenceInput): Promise<RecurrenceRule> {
    const timestamp = nowUtc();
    let rule!: RecurrenceRule;

    await db.transaction('rw', writeTables, async () => {
      const task = await requireTask(id);
      if (task.kind === 'note') {
        throw new InvariantError([{ code: 'T5', message: '便條不能設定重複規則' }]);
      }

      rule = {
        id: newUuid(),
        frequency: input.frequency,
        weekdays: input.weekdays ?? null,
        monthDay: input.monthDay ?? null,
        yearMonth: input.yearMonth ?? null,
        yearDay: input.yearDay ?? null,
        endDate: input.endDate ?? null,
        ...newAudit(timestamp),
      };
      await db.recurrenceRules.add(rule);

      const previous = task.recurrenceRuleId;
      await db.tasks.put({
        ...task,
        recurrenceRuleId: rule.id,
        ...bumpAudit(task, timestamp),
      });
      if (previous) await removeOrphanRule(previous);
      await markDataMutation(db, timestamp);
    });

    return rule;
  }

  /** 修改規則本身，影響之後所有依此規則產生的任務（spec 9.4）。 */
  async function updateRecurrence(ruleId: Uuid, patch: Partial<RecurrenceInput>): Promise<void> {
    const timestamp = nowUtc();
    await db.transaction('rw', writeTables, async () => {
      const current = await db.recurrenceRules.get(ruleId);
      if (!current) throw new NotFoundError('重複規則', ruleId);
      await db.recurrenceRules.put({ ...current, ...patch, ...bumpAudit(current, timestamp) });
      await markDataMutation(db, timestamp);
    });
  }

  /** 「只修改本次」：解除 recurrenceRuleId，規則本身不動。 */
  async function detachRecurrence(id: Uuid): Promise<void> {
    const timestamp = nowUtc();
    await db.transaction('rw', writeTables, async () => {
      const task = await requireTask(id);
      const previous = task.recurrenceRuleId;
      await db.tasks.put({ ...task, recurrenceRuleId: null, ...bumpAudit(task, timestamp) });
      if (previous) await removeOrphanRule(previous);
      await markDataMutation(db, timestamp);
    });
  }

  /** 沒有任何任務參照的規則在交易結束時一併刪除，避免孤兒（data-model 3.5）。 */
  async function removeOrphanRule(ruleId: Uuid): Promise<void> {
    const stillUsed = await db.tasks.where('recurrenceRuleId').equals(ruleId).count();
    if (stillUsed === 0) await db.recurrenceRules.delete(ruleId);
  }

  return {
    create,
    update,
    appendProgress,
    setStatus,
    complete,
    close,
    cancel,
    uncomplete,
    removeFromBoard,
    putOnBoard,
    archive,
    softDelete,
    restore,
    listActive,
    listDeleted,
    listNotesFor,
    get,
    setRecurrence,
    updateRecurrence,
    detachRecurrence,
    getRecurrence: (ruleId: Uuid) => db.recurrenceRules.get(ruleId),
  };
}

export type TaskRepository = ReturnType<typeof createTaskRepository>;
export const taskRepository = createTaskRepository();
